import React, { useState, useMemo, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";

import {
    Button,
    Card,
    CardBody,
    Col,
    Container,
    Input,
    Row,
} from "reactstrap";
import CommonTable from "components/Common/TableContainer";
import { submitUserIPSettingFilterData, updateUserIPSettingAllowed } from "model/UserIPSettingsModel";
import { toast } from "react-toastify";
import { useUserIPSettingsContext } from "libs/context/UserIPSettingsContext";
import AllCommonFilterModal from "pages/AllCommonFilter/all-common-filter";
import Loaders from "components/Common/Loaders";
import { DEFAULT_PAGE_SIZE } from "helpers/GeneralHelper";
import { useCommonTableSettingsPageSize } from "hooks/useSettingsReadyTablePageSize";


const strOrNull = (value) => {
    if (value === null || value === undefined) return null;
    const normalized = String(value).trim();
    return normalized === "" ? null : normalized;
};

const normalizeUserIPSettingsFilterPayload = (raw = {}) => {
    const out = { ...raw };
    ["nameContains", "fromDate", "toDate"].forEach((key) => {
        if (key in out) out[key] = strOrNull(out[key]);
    });
    return out;
};

const UserIPSettings = () => {
    const { fetchUserIPSettingsContextData, userIPSettingsContextData } = useUserIPSettingsContext() || {};
    const { pageSize: settingsPageSize, settingsReady } = useCommonTableSettingsPageSize("User IP Settings");
    const initialFilterFetchedRef = useRef(false);

    const [userIPSettingsData, setUserIPSettingsData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tableMetaData, setTableMetadata] = useState();
    const [commonFilterModalOpen, setCommonFilterModalOpen] = useState(false);
    const [filterForm, setFilterForm] = useState({
        nameContains: "",
        fromDate: "",
        toDate: "",
    });
    const [requestPayload, setRequestPayload] = useState({
        pageNumber: 1,
        pageSize: DEFAULT_PAGE_SIZE,
        nameContains: null,
        fromDate: null,
        toDate: null,
    });
    const [paginationMeta, setPaginationMeta] = useState({
        pageNumber: 1,
        pageSize: DEFAULT_PAGE_SIZE,
        totalPages: 0,
        totalRecords: 0,
    });

    const filteredData = useMemo(() => userIPSettingsData, [userIPSettingsData]);

    useEffect(() => {
        fetchUserIPSettingsContextData();
    }, []);

    useEffect(() => {
        if (!settingsReady || initialFilterFetchedRef.current) return;
        initialFilterFetchedRef.current = true;
        const pageSize = Number(settingsPageSize) || DEFAULT_PAGE_SIZE;
        setRequestPayload((previous) => ({ ...previous, pageSize }));
        setPaginationMeta((previous) => ({ ...previous, pageSize }));
        getUserIPSettings({ pageNumber: 1, pageSize });
    }, [settingsReady, settingsPageSize]);

    useEffect(() => {
        const valueReModified = userIPSettingsContextData?.table?.columns?.map(col => ({
            Header: col?.title,
            accessor: col?.field,
        }));
        setTableMetadata(valueReModified);
    }, [userIPSettingsContextData]);

    const getUserIPSettings = async (payloadOverrides = {}) => {
        setLoading(true);
        const payload = normalizeUserIPSettingsFilterPayload({
            ...requestPayload,
            ...payloadOverrides,
        });
        const response = await submitUserIPSettingFilterData(payload);
        const responseData = response?.data || {};
        const listData = responseData?.records || responseData || [];

        if (response) {
            setUserIPSettingsData(Array.isArray(listData) ? listData : []);
            setPaginationMeta({
                pageNumber: Number(responseData?.pageNumber) || Number(payload?.pageNumber) || 1,
                pageSize: Number(responseData?.pageSize) || Number(payload?.pageSize) || DEFAULT_PAGE_SIZE,
                totalPages: Number(responseData?.totalPages) || 0,
                totalRecords: Number(responseData?.totalRecords) || 0,
            });
            setRequestPayload(payload);
        } else {
            setUserIPSettingsData([]);
        }
        setLoading(false);
    };

    const buildPayloadFromUpperForm = (form = {}) =>
        normalizeUserIPSettingsFilterPayload({
            pageNumber: 1,
            pageSize: requestPayload.pageSize || DEFAULT_PAGE_SIZE,
            nameContains: strOrNull(form?.search),
            fromDate: strOrNull(form?.fromDate),
            toDate: strOrNull(form?.toDate),
        });

    const handleUpperSearch = () => {
        getUserIPSettings(buildPayloadFromUpperForm(filterForm));
    };

    const handleFilterSubmit = async (data) => {
        await getUserIPSettings({
            ...data,
            pageNumber: 1,
            pageSize: paginationMeta?.pageSize || DEFAULT_PAGE_SIZE,
        });
        setCommonFilterModalOpen(false);
    };

    const handlePageChange = async (page) => {
        await getUserIPSettings({ pageNumber: page });
    };

    const handleRowsPerPageChange = async (rows) => {
        await getUserIPSettings({ pageNumber: 1, pageSize: rows });
    };

    const handleToggleAllowed = async (row, value) => {
        const rowId = row?.id ?? row?.Id;
        const response = await updateUserIPSettingAllowed({
            id: String(rowId ?? ""),
            isAllowed: value,
        });

        if (response?.success) {
            setUserIPSettingsData((prev) =>
                (prev || []).map((item) => {
                    const itemId = item?.id ?? item?.Id;
                    return String(itemId) === String(rowId)
                        ? { ...item, isAllowed: value }
                        : item;
                })
            );
            toast.success(response?.message || "Updated successfully");
        }
    };

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Card>
                        <CardBody>
                            <div className="d-flex justify-content-between align-items-center">
                                <h5 className="fw-bold mb-3">User IP Settings</h5>
                            </div>
                            <hr />

                            <div className="upper-filter-bar manage-user-filter-strip">
                                <Row className="g-2 g-md-3 align-items-end">
                                    {/* <Col xs="12" md="6" lg={3}>
                                        <span className="manage-user-filter-label">Search</span>
                                        <Input
                                            type="text"
                                            name="search"
                                            placeholder="Search"
                                            className="border rounded w-100 category-search-input"
                                            value={filterForm.search}
                                            onChange={(e) => {
                                                const nextValue = e?.target?.value || "";
                                                setFilterForm((prev) => ({ ...prev, search: nextValue }));
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") {
                                                    handleUpperSearch();
                                                }
                                            }}
                                        />
                                    </Col> */}
                                    <Col xs="12" md="6" lg={2}>
                                        <span className="manage-user-filter-label">From date</span>
                                        <Input
                                            type="date"
                                            name="fromDate"
                                            className="border rounded w-100 category-search-input"
                                            value={filterForm.fromDate}
                                            onChange={(e) =>
                                                setFilterForm((prev) => ({
                                                    ...prev,
                                                    fromDate: e?.target?.value || "",
                                                }))
                                            }
                                        />
                                    </Col>
                                    <Col xs="12" md="6" lg={2}>
                                        <span className="manage-user-filter-label">To date</span>
                                        <Input
                                            type="date"
                                            name="toDate"
                                            className="border rounded w-100 category-search-input"
                                            value={filterForm.toDate}
                                            onChange={(e) =>
                                                setFilterForm((prev) => ({
                                                    ...prev,
                                                    toDate: e?.target?.value || "",
                                                }))
                                            }
                                        />
                                    </Col>
                                    <Col xs="12" md="6" lg={2}>
                                        <span className="manage-user-filter-label" aria-hidden="true">&nbsp;</span>
                                        <Button
                                            color="primary"
                                            className="w-100 manage-user-search-btn"
                                            type="button"
                                            onClick={handleUpperSearch}
                                        >
                                            <i className="fa fa-search me-1" />
                                            Search
                                        </Button>
                                    </Col>
                                </Row>
                            </div>

                            {loading ? (
                                <Loaders />
                            ) : tableMetaData?.length ? (
                                <CommonTable
                                    columns={tableMetaData}
                                    data={filteredData}
                                    isActionRequired={false}
                                    isCheckboxLeftSide={false}
                                    isPagination={true}
                                    currentPage={paginationMeta?.pageNumber}
                                    rowsPerPage={paginationMeta?.pageSize}
                                    totalPages={paginationMeta?.totalPages}
                                    totalRecords={paginationMeta?.totalRecords}
                                    onPageChange={handlePageChange}
                                    onPageSizeChange={handleRowsPerPageChange}
                                    setCommonFilterModalOpen={setCommonFilterModalOpen}
                                    pageName="User IP Settings"
                                    onToggleAllowed={(row, value) => handleToggleAllowed(row, value)}
                                />
                            ) : (
                                <Loaders />
                            )}
                        </CardBody>
                    </Card>

                    <AllCommonFilterModal
                        setCommonFilterModalOpen={setCommonFilterModalOpen}
                        commonFilterModalOpen={commonFilterModalOpen}
                        modalTitle="User IP Settings"
                        handleSubmit={handleFilterSubmit}
                        filterData={userIPSettingsContextData?.filters}
                    />
                </Container>
            </div>
        </React.Fragment>
    );
};

UserIPSettings.propTypes = {
    orders: PropTypes.array,
    onGetOrders: PropTypes.func,
};

export default withRouter(UserIPSettings);
